import { getDeviceEnv } from "@frontend/utils/device.utils";
import {
	canPromptInstall,
	type InstallOutcome,
	installPromptSnapshot,
	promptInstall,
	subscribeInstallPrompt,
	wasInstalled,
} from "@frontend/utils/install_prompt.utils";
import { useCallback, useState, useSyncExternalStore } from "react";

/** @public — named in the declaration of an exported value (TS4023). */
export interface PwaInstallState {
	/** True when the browser's install dialog can be opened (Chromium). */
	isInstallAvailable: boolean;
	/** True when running as the installed app (or just installed). */
	isStandalone: boolean;
	/** True on iPhone / iPad: no install API, the user adds it from Share. */
	isAppleMobile: boolean;
	/** The install sheet (Install button, or Add-to-Home-Screen steps on iOS). */
	sheetOpen: boolean;
	openSheet: () => void;
	closeSheet: () => void;
	/**
	 * Install: the browser's dialog where there is one; on iOS (or a browser
	 * without one) the sheet with the manual steps.
	 */
	install: () => Promise<InstallOutcome>;
}

export const usePwaInstall = (): PwaInstallState => {
	useSyncExternalStore(subscribeInstallPrompt, installPromptSnapshot, installPromptSnapshot);
	const device = getDeviceEnv();
	const [sheetOpen, setSheetOpen] = useState(false);
	const openSheet = useCallback(() => setSheetOpen(true), []);
	const closeSheet = useCallback(() => setSheetOpen(false), []);

	const install = useCallback(async (): Promise<InstallOutcome> => {
		if (canPromptInstall()) return promptInstall();
		setSheetOpen(true);
		return "unavailable";
	}, []);

	return {
		isInstallAvailable: canPromptInstall(),
		isStandalone: device.isStandalone || wasInstalled(),
		isAppleMobile: device.isIOS,
		sheetOpen,
		openSheet,
		closeSheet,
		install,
	};
};
